import { isEmpty } from "common/Utils/formik";
import Field from "Components/Field";
import { v4 as uuidv4 } from "uuid";
import { Title, InputWrapper } from "../SendUsStory.styled";
import UploadImage from "Components/UploadImage";
import TextArea from "Components/TextArea";
import Button from "Components/Button";
import Input from "Components/Input";
import Axios from "axios";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useState, FC, useContext, useEffect } from "react";
import {
  Info,
  LegalWrapper,
  UploadWrapper,
  ButtonWrapper,
  TextAreaWrapper,
  EmailWrapper,
} from "./SendUsStoryForm.styled";
import { publicationTypes } from "common/context/Publication/PublicationContext";
import { PublicationContext } from "common/context/Publication";
import { defaultBreakpoints } from "styled-media-query";

const SignSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Required"),
  name: Yup.string().required("Required"),
  body: Yup.string().required("Required"),
  phone: Yup.number().required("Required"),
});

interface Props {
  setSuccess: any;
}

export type WindowSize = {
  width: number | undefined;
  height: number | undefined;
};

const SendUsStoryForm: FC<Props> = ({ setSuccess }) => {
  const [files, setFiles] = useState([]);
  const [cropData, setCropData] = useState("#");
  const { config } = useContext<publicationTypes>(PublicationContext);
  const [size, setSize] = useState<WindowSize>({
    width: undefined,
    height: undefined,
  });

  const useWindowSize = (): WindowSize => {
    useEffect(() => {
      function handleResize(): void {
        setSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      }
      handleResize();
      window.addEventListener("resize", handleResize);
      return (): void => window.removeEventListener("resize", handleResize);
    }, []);

    return size;
  };
  useWindowSize();

  return (
    <>
      <Title>Your story</Title>
      <Info>
        Do you know someone who deserves their place in the spotlight? Is there
        a burning issue you think we should cover? Or have you captured a
        stunning picture or video you’d like to share?{" "}
        <span className="bold">Then we want to hear from you! </span>
        <br />
        <br />
        Get in touch using the form below, providing as much information as
        possible and your contact details in case we need more information.
      </Info>

      <Formik
        initialValues={{
          body: "",
          name: "",
          email: "",
          phone: "",
        }}
        onSubmit={async (values, { resetForm }) => {
          let id = uuidv4();
          const url = `https://flowservice-${process.env.NEXT_PUBLIC_ENV_STATE}.s3.eu-west-2.amazonaws.com/send-a-story/${id}.png`;

          try {
            if (cropData === "#" && files.length === 0) {
              await Axios.post(`/api/ses`, {
                from: config.your_story,
                to: config.your_story,
                body: `<body><h1>Publication user has sent story to: ${config.pub}</h1><br/><p>Writers Information:</p><p>name:${values.name}</p><p>email: ${values.email}</p> <p>phone: ${values.phone}</p><br/><br/><p>Story:</p> <p>${values.body}</p></body> `,
                subject: `new story for ${config.pub}`,
              });
              setSuccess(true);
            } else {
              await Axios.post(`/api/ses`, {
                from: config.your_story,
                to: config.your_story,
                body: `<body><h1>Publication user has sent story to: ${config.pub}</h1><br/><p>Writers Information:</p><p>name:${values.name}</p> <p>email: ${values.email}</p> <p>phone: ${values.phone}</p><img src="${url}" style="max-width: 500px; width: 100%; height: auto;"/><br/><br/><p>Story:</p> <p>${values.body}</p></body> `,
                subject: `new story for ${config.pub}`,
              });

              const imageBlob = cropData === "#" ? files[0] : cropData;

              const uploadUrl = await Axios.post("/api/s3", {
                imageId: `send-a-story/${id}.png`,
              });
              await Axios.put(uploadUrl.data.res, imageBlob, {
                headers: {
                  "Content-Type": "image/png",
                },
              });
              setSuccess(true);
            }
          } catch (e) {
            console.log(e);
            setSuccess(true);
          }
        }}
        validationSchema={SignSchema}
      >
        {({
          values,
          handleChange,
          handleSubmit,
          touched,
          errors,
          isSubmitting,
        }) => (
          <Form onSubmit={handleSubmit}>
            <UploadWrapper>
              <TextAreaWrapper>
                <TextArea
                  type="form"
                  name="body"
                  label="Tell us your news"
                  handleChange={handleChange}
                  placeholder="Please provide us with your newsworthy details"
                  required
                />
              </TextAreaWrapper>
              <UploadImage
                cropData={cropData}
                setCropData={setCropData}
                files={files}
                setFiles={setFiles}
              />
            </UploadWrapper>
            <InputWrapper>
              <div className="input-inner-wrapper">
                <Input
                  placeholder="Insert name"
                  inputStyles="frosted"
                  label="Name"
                  name="name"
                  handleChange={handleChange}
                  required={true}
                  className="input-name"
                />
                <Field
                  placeholder="Insert phone number"
                  inputStyles="frosted"
                  label="Phone number"
                  inputType="tel"
                  handleChange={handleChange}
                  bottom_error={touched.phone && errors.phone}
                  required={true}
                  name="phone"
                  pattern="^(?:0|\(?\+33\)?\s?|0033\s?)[1-79](?:[\.\-\s]?\d\d){4}$"
                  className="input-phone"
                />
              </div>
              <EmailWrapper>
                <Field
                  placeholder="Insert email"
                  inputStyles="frosted"
                  label="Email"
                  required={true}
                  name="email"
                  bottom_error={touched.email && errors.email}
                  handleChange={handleChange}
                />
              </EmailWrapper>
            </InputWrapper>
            <LegalWrapper>
              By sending a story you confirm that you have read and agree to our{" "}
              <a href="/terms">Terms and Conditions</a> and{" "}
              <a href="/privacy">Privacy Policy</a>.
            </LegalWrapper>
            <ButtonWrapper>
              <Button
                disabled={
                  isSubmitting ||
                  isEmpty(errors) ||
                  values.email === "" ||
                  values.phone === "" ||
                  values.body === "" ||
                  values.name === ""
                }
                buttonType="submit"
                buttonStyles="primary"
                fullwidth={size.width && size.width > 768 ? false : true}
              >
                Send Your Story
              </Button>
            </ButtonWrapper>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default SendUsStoryForm;
